
import React, { useState } from 'react';
import { Edit3, X, ChevronRight, Building2, SortAsc } from 'lucide-react';
import { Person } from '../../types';
// Fix: Use namespace import to bypass missing exported member errors in react-router-dom
import * as ReactRouterDom from 'react-router-dom';

const { useNavigate } = ReactRouterDom as any;

interface PersonTableProps {
  people: Person[] | undefined;
  isLoading: boolean;
  onEdit: (person: Person) => void;
  onDelete: (person: Person) => void;
}

const formatPhoneNumber = (phone: string | null) => {
  if (!phone) return '—';
  const cleaned = ('' + phone).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}`;
  }
  return phone;
};

export const PersonTable: React.FC<PersonTableProps> = ({ 
  people, 
  isLoading, 
  onEdit, 
  onDelete
}) => {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState<string>('first_name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({
    name: 250,
    email: 200,
    phone: 150,
    org: 200,
    affiliation: 150,
    actions: 120
  });

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedPeople = React.useMemo(() => {
    if (!people) return [];
    return [...people].sort((a, b) => {
      let valA = (a[sortField as keyof Person] as string || '').toLowerCase();
      let valB = (b[sortField as keyof Person] as string || '').toLowerCase();
      
      if (sortField === 'org') {
        valA = (a.organizations?.name || '').toLowerCase();
        valB = (b.organizations?.name || '').toLowerCase();
      }
      if (sortField === 'affiliation') {
        valA = (a.affiliations?.label || '').toLowerCase();
        valB = (b.affiliations?.label || '').toLowerCase();
      }

      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [people, sortField, sortDirection]);

  const handleResize = (column: string, width: number) => {
    setColumnWidths(prev => ({ ...prev, [column]: Math.max(100, width) }));
  };

  const SortIndicator = ({ field }: { field: string }) => {
    if (sortField !== field) return <SortAsc size={10} className="ml-1 opacity-20" />;
    return sortDirection === 'asc' 
      ? <SortAsc size={10} className="ml-1 text-blue-600" /> 
      : <SortAsc size={10} className="ml-1 text-blue-600 rotate-180" />;
  };

  if (isLoading) {
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <tbody className="divide-y divide-slate-100">
            {[1, 2, 3, 4, 5, 6, 7].map((i) => (
              <tr key={i} className="animate-pulse">
                <td className="px-5 py-2.5"><div className="h-3 bg-slate-100 rounded w-full"></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (!people || people.length === 0) {
    return (
      <div className="px-8 py-32 text-center">
        <p className="uppercase font-black text-slate-200 text-[10px] tracking-[0.2em] italic">
          No matching identity records discovered
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left border-collapse table-fixed">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-100 text-slate-400 text-[8px] font-black uppercase tracking-[0.2em] select-none">
            <th className="px-6 py-2.5 relative group" style={{ width: columnWidths.name }}>
              <div className="flex items-center cursor-pointer hover:text-slate-900 transition-colors" onClick={() => handleSort('first_name')}>
                Name <SortIndicator field="first_name" />
              </div>
              <div className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 transition-colors" onMouseDown={(e) => {
                const startX = e.pageX;
                const startWidth = columnWidths.name;
                const onMouseMove = (moveEvent: MouseEvent) => {
                  handleResize('name', startWidth + (moveEvent.pageX - startX));
                };
                const onMouseUp = () => {
                  document.removeEventListener('mousemove', onMouseMove);
                  document.removeEventListener('mouseup', onMouseUp);
                };
                document.addEventListener('mousemove', onMouseMove);
                document.addEventListener('mouseup', onMouseUp);
              }} />
            </th>
            <th className="px-6 py-2.5 relative group" style={{ width: columnWidths.email }}>
              <div className="flex items-center cursor-pointer hover:text-slate-900 transition-colors" onClick={() => handleSort('email')}>
                Email <SortIndicator field="email" />
              </div>
              <div className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 transition-colors" onMouseDown={(e) => {
                const startX = e.pageX;
                const startWidth = columnWidths.email;
                const onMouseMove = (moveEvent: MouseEvent) => {
                  handleResize('email', startWidth + (moveEvent.pageX - startX));
                };
                const onMouseUp = () => {
                  document.removeEventListener('mousemove', onMouseMove);
                  document.removeEventListener('mouseup', onMouseUp);
                };
                document.addEventListener('mousemove', onMouseMove);
                document.addEventListener('mouseup', onMouseUp);
              }} />
            </th>
            <th className="px-6 py-2.5 relative group" style={{ width: columnWidths.phone }}>
              <div className="flex items-center cursor-pointer hover:text-slate-900 transition-colors" onClick={() => handleSort('phone')}>
                Phone <SortIndicator field="phone" />
              </div>
              <div className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 transition-colors" onMouseDown={(e) => {
                const startX = e.pageX;
                const startWidth = columnWidths.phone;
                const onMouseMove = (moveEvent: MouseEvent) => {
                  handleResize('phone', startWidth + (moveEvent.pageX - startX));
                };
                const onMouseUp = () => {
                  document.removeEventListener('mousemove', onMouseMove);
                  document.removeEventListener('mouseup', onMouseUp);
                };
                document.addEventListener('mousemove', onMouseMove);
                document.addEventListener('mouseup', onMouseUp);
              }} />
            </th>
            <th className="px-6 py-2.5 relative group" style={{ width: columnWidths.org }}>
              <div className="flex items-center cursor-pointer hover:text-slate-900 transition-colors" onClick={() => handleSort('org')}>
                Organization <SortIndicator field="org" />
              </div>
              <div className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 transition-colors" onMouseDown={(e) => {
                const startX = e.pageX;
                const startWidth = columnWidths.org;
                const onMouseMove = (moveEvent: MouseEvent) => {
                  handleResize('org', startWidth + (moveEvent.pageX - startX));
                };
                const onMouseUp = () => {
                  document.removeEventListener('mousemove', onMouseMove);
                  document.removeEventListener('mouseup', onMouseUp);
                };
                document.addEventListener('mousemove', onMouseMove);
                document.addEventListener('mouseup', onMouseUp);
              }} />
            </th>
            <th className="px-6 py-2.5 relative group" style={{ width: columnWidths.affiliation }}>
              <div className="flex items-center cursor-pointer hover:text-slate-900 transition-colors" onClick={() => handleSort('affiliation')}>
                Affiliation <SortIndicator field="affiliation" />
              </div>
              <div className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 transition-colors" onMouseDown={(e) => {
                const startX = e.pageX;
                const startWidth = columnWidths.affiliation;
                const onMouseMove = (moveEvent: MouseEvent) => {
                  handleResize('affiliation', startWidth + (moveEvent.pageX - startX));
                };
                const onMouseUp = () => {
                  document.removeEventListener('mousemove', onMouseMove);
                  document.removeEventListener('mouseup', onMouseUp);
                };
                document.addEventListener('mousemove', onMouseMove);
                document.addEventListener('mouseup', onMouseUp);
              }} />
            </th>
            <th className="px-6 py-2.5 text-right" style={{ width: columnWidths.actions }}>Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50 bg-white">
          {sortedPeople.map((person) => (
            <tr 
              key={person.id} 
              className="hover:bg-slate-50/80 transition-all group cursor-pointer"
              onClick={() => navigate(`/people/${person.id}`)}
            >
              <td className="px-6 py-2.5 overflow-hidden">
                <p className="font-bold text-slate-900 uppercase text-[11px] tracking-tight break-words">
                  {person.first_name} {person.last_name}
                </p>
              </td>
              <td className="px-6 py-2.5 overflow-hidden">
                <span className="text-[10px] font-medium text-slate-500 lowercase truncate block">{person.email || '—'}</span>
              </td>
              <td className="px-6 py-2.5 overflow-hidden">
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest truncate block">
                  {formatPhoneNumber(person.phone)}
                </span>
              </td>
              <td className="px-6 py-2.5 overflow-hidden">
                <div className="flex items-center gap-2 text-slate-900 font-bold uppercase text-[9px] tracking-tight truncate">
                  <Building2 size={10} className="text-slate-300 shrink-0" />
                  <span className="truncate">{person.organizations?.name || 'Unknown'}</span>
                </div>
              </td>
              <td className="px-6 py-2.5 overflow-hidden">
                <div className="flex items-center gap-2 text-slate-900 font-bold uppercase text-[9px] tracking-tight truncate">
                  <span className="px-2 py-0.5 bg-slate-100 rounded-full text-[8px] truncate">
                    {person.affiliations?.label || 'General'}
                  </span>
                </div>
              </td>
              <td className="px-6 py-2.5 text-right">
                <div className="flex items-center justify-end gap-1.5 opacity-30 group-hover:opacity-100 transition-opacity">
                  <button 
                    onClick={(e) => { e.stopPropagation(); onEdit(person); }} 
                    className="p-1 bg-white border border-slate-200 rounded text-slate-400 hover:text-slate-900 hover:border-slate-900 transition-all"
                    title="Edit Record"
                  >
                    <Edit3 size={11} />
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); onDelete(person); }} 
                    className="p-1 bg-white border border-slate-200 rounded text-slate-400 hover:text-rose-600 hover:border-rose-600 transition-all"
                    title="Delete Record"
                  >
                    <X size={11} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
